import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { apiCall } from '../lib/api';
import { trackPurchase } from '../lib/analytics';

interface OrderDetail {
  id: string;
  shopify_order_number: string;
  total: number;
  currency: string;
  financial_status: string | null;
  fulfillment_status: string | null;
  line_items?: Array<{
    title: string;
    quantity: number;
    price?: string | number;
    image?: { src: string } | null;
  }>;
  created_at: string;
  order_status_url?: string | null;
  points_earned?: number | null;
}

export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery<{ order: OrderDetail }>({
    queryKey: ['order', id],
    queryFn: () => apiCall<{ order: OrderDetail }>('get', `/customers/me/orders/${id}`),
    enabled: !!id,
  });

  // Fire purchase DataLayer event once per order view session (idempotent via sessionStorage)
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current || !q.data?.order) return;
    const o = q.data.order;
    const key = `purchase_fired_${o.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    fired.current = true;
    trackPurchase(
      o.shopify_order_number ?? String(o.id),
      (o.line_items ?? []).map((i) => ({
        item_id: i.title,
        item_name: i.title,
        price: Number(i.price ?? 0),
        quantity: i.quantity,
        item_brand: 'mesoestetic',
      })),
      Number(o.total ?? 0),
    );
  }, [q.data]);

  if (q.isLoading) return <div className="p-6 text-center text-gray-500">กำลังโหลด...</div>;
  if (!q.data?.order) return <div className="p-6 text-center text-gray-500">ไม่พบคำสั่งซื้อ</div>;

  const o = q.data.order;
  const items = o.line_items ?? [];

  return (
    <div className="max-w-md mx-auto px-4 py-4 pb-24">
      <Link to="/orders" className="block text-sm text-gray-500 mb-2">← ย้อนกลับ</Link>

      <div className="card">
        <div className="font-bold text-lg">คำสั่งซื้อ {o.shopify_order_number}</div>
        <div className="text-xs text-gray-500 mt-1">
          {new Date(o.created_at).toLocaleString('th-TH')}
        </div>

        <div className="mt-3 flex gap-2 flex-wrap">
          {o.financial_status && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
              ชำระเงิน: {o.financial_status}
            </span>
          )}
          {o.fulfillment_status && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              จัดส่ง: {o.fulfillment_status}
            </span>
          )}
        </div>
      </div>

      <div className="card mt-3">
        <div className="font-semibold mb-2">รายการสินค้า</div>
        {items.map((item, i) => (
          <div key={i} className="flex gap-2 py-2 border-b border-gray-100 last:border-0">
            {item.image?.src && (
              <img src={item.image.src} className="w-12 h-12 rounded object-cover" alt="" />
            )}
            <div className="flex-1 text-sm">
              <div className="font-medium">{item.title}</div>
              <div className="text-xs text-gray-500">x {item.quantity}</div>
            </div>
            {item.price !== undefined && (
              <div className="text-sm font-semibold">
                ฿{(parseFloat(String(item.price)) * item.quantity).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="card mt-3 flex justify-between items-center">
        <span className="text-sm">ยอดรวม</span>
        <span className="text-xl font-bold">
          ฿{o.total.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </span>
      </div>

      {o.points_earned != null && o.points_earned > 0 && (
        <div className="card mt-3 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
          <div className="text-xs opacity-80">คะแนนที่ได้รับจากออเดอร์นี้</div>
          <div className="text-2xl font-bold">+{o.points_earned.toLocaleString('th-TH')} คะแนน</div>
        </div>
      )}

      {o.order_status_url && (
        <a href={o.order_status_url} target="_blank" rel="noreferrer"
           className="btn btn-outline btn-block mt-3">
          ดูสถานะที่ Shopify →
        </a>
      )}
    </div>
  );
}
